/**
 * План месяца: версия, которая хранится, а не только считается.
 *
 * Три вещи, которые легко спутать и очень дорого путать:
 *
 * · **факт** — что действительно случилось, по операциям;
 * · **сохранённый план** — снимок сумм, он не меняется сам;
 * · **прогноз** — что получается из сегодняшних данных прямо сейчас.
 *
 * Раньше снимок делала кнопка «Принять план». Человек не понимал, что она
 * значит, а без нажатия сравнивать было не с чем — и правка дохода
 * переписывала ответ «я уложился?» за прошлые месяцы.
 *
 * Теперь снимок делается сам: пока месяц идёт, план следует за правками
 * человека, а когда месяц закончился, его последняя версия остаётся как
 * была. Новая версия **добавляется**, прошлая остаётся историей.
 */
import {
  ZERO, acceptPlan, acceptedFor, computeBudget, decisionOf, monthPeriod, paidOnOf,
  type Money, type PlainDate,
  type AcceptedPlan, type Answer, type BudgetInput, type FinancialPlan, type PlanLineKind, type PlanMode,
  type PlanPeriod,
} from '@skoka/core';
import { saveAcceptedPlan, type AppState } from './store.js';

/** Проведённые выплаты себе — по дню, когда деньги двигались. */
export const selfPayoutsOf = (state: AppState): { date: PlainDate; amount: Money }[] =>
  (state.stablePayouts ?? [])
    .filter((x) => decisionOf(x) === 'paid' && x.amount > ZERO)
    .map((x) => ({ date: paidOnOf(x), amount: x.amount }));

/** Режим плана: без выбора человека — сбалансированный. */
export const modeOf = (state: AppState): PlanMode => state.planMode ?? 'balanced';

/** План из состояния: с сохранённой версией или без, за месяц или за период. */
export const budgetWith = (
  state: AppState,
  accepted: AcceptedPlan | null,
  period?: PlanPeriod,
  extra: Pick<BudgetInput, 'periodLines' | 'factsFrom' | 'factsTo' | 'livesOnPayouts'> = {},
): Answer<{ plan: FinancialPlan }> => computeBudget({
  today: state.today,
  ...(period ? { period } : {}),
  ...extra,
  // Факты — тем же подсчётом, что «Итоги месяца»: цели по журналу, выплаты
  // себе по дню движения денег, перевод на кредитку — не расход.
  goalEvents: state.goalEvents,
  selfPayouts: selfPayoutsOf(state),
  creditCardIds: state.accounts.filter((a) => a.kind === 'credit_card').map((a) => a.id),
  incomeStreams: state.incomeStreams,
  obligations: state.obligations,
  goals: state.goals,
  accounts: state.accounts,
  creditTerms: state.creditTerms,
  transactions: state.transactions,
  needBaseline: state.baselines.need,
  wantBaseline: state.baselines.want,
  mode: modeOf(state),
  accepted,
});

/**
 * План периода из текущего состояния.
 *
 * Может ответить «не знаю»: без обычных трат или без источника дохода
 * план строился бы на нуле, а это не план, а обвинение в дефиците.
 */
export const budgetOf = (state: AppState): Answer<{ plan: FinancialPlan }> =>
  budgetWith(state, acceptedPlanOf(state));

/** Сохранённая версия этого периода, если она есть. */
export const acceptedPlanOf = (state: AppState): AcceptedPlan | null =>
  acceptedFor(state.acceptedPlans, monthPeriod(state.today));

const uid = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `plan_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const LINES: PlanLineKind[] = ['income', 'obligations', 'need', 'want', 'goals', 'free'];

/**
 * Сохранить план этого месяца, если его суммы изменились.
 *
 * Расчёт идёт **без** сохранённой версии: с ней `computeBudget` вернул бы её
 * же суммы, и новая версия повторила бы старую. Так и было с «Принять
 * изменение» в цели — после первого принятия план больше не обновлялся.
 *
 * Отвечает, записано ли что-нибудь: тогда состояние надо перечитать.
 */
export async function keepMonthPlan(state: AppState): Promise<boolean> {
  const fresh = budgetWith(state, null);
  if (!fresh.known) return false;
  const previous = acceptedPlanOf(state);
  if (previous && LINES.every((k) => previous.lines[k] === fresh.plan.lines[k].planned)) return false;

  await saveAcceptedPlan(acceptPlan({
    id: uid(),
    plan: fresh.plan,
    mode: modeOf(state),
    now: new Date().toISOString(),
    previous,
  }));
  return true;
}
